import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from './useCurrentAppUser';

export interface PendingConfirmation {
  id: string;
  contactId: string | null;
  contactName: string | null;
  dealId: string | null;
  leadId: string | null;
  acceptedAt: string;
  completedAt?: string;
  meetingUrl: string | null;
  requestedBy: string;
  requestedByName: string | null;
  source: 'ring' | 'scheduled';
  scheduledTitle?: string;
}

export function usePendingConnectionConfirmations() {
  const { data: appUser } = useCurrentAppUser();
  const [pendingConfirmations, setPendingConfirmations] = useState<PendingConfirmation[]>([]);
  const [loading, setLoading] = useState(true);

  const loadPendingConfirmations = useCallback(async () => {
    if (!appUser?.id) {
      setPendingConfirmations([]);
      setLoading(false);
      return;
    }

    try {
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      // Fetch from both sources in parallel
      const [ringResult, scheduledResult] = await Promise.all([
        // 1. Video Ring Requests (Video Now) - permanent until confirmed
        supabase
          .from('video_call_ring_requests' as any)
          .select(`
            id,
            contact_id,
            deal_id,
            lead_id,
            accepted_at,
            meeting_url,
            requested_by,
            connection_confirmed
          `)
          .eq('accepted_by', appUser.id)
          .eq('status', 'accepted')
          .is('connection_confirmed', null)
          .gte('accepted_at', twentyFourHoursAgo)
          .order('accepted_at', { ascending: false }),

        // 2. Scheduled Video Calls - completed in last 24 hours
        supabase
          .from('calls')
          .select(`
            id,
            contact_id,
            deal_id,
            lead_id,
            completed_at,
            meeting_url,
            created_by,
            scheduled_by,
            title,
            handled_by_app_user_id,
            attendees,
            connection_confirmed
          `)
          .eq('channel', 'video')
          .eq('status', 'completed')
          .is('connection_confirmed', null)
          .gte('completed_at', twentyFourHoursAgo)
          .neq('title', 'Immediate Video Call')
          .order('completed_at', { ascending: false })
      ]);

      if (ringResult.error) {
        console.error('Error loading ring confirmations:', ringResult.error);
      }

      if (scheduledResult.error) {
        console.error('Error loading scheduled confirmations:', scheduledResult.error);
      }

      const ringRequests = (ringResult.data || []) as any[];
      const scheduledCalls = (scheduledResult.data || []) as any[];

      // Filter scheduled calls where current user is the HOST (created_by)
      // The confirmation modal should ONLY appear for the HOST (Anfitrião/Consultant)
      // who was selected as the person to run the meeting, NOT the SDR who scheduled it
      const filteredScheduledCalls = scheduledCalls.filter((call: any) => {
        // Modal de confirmação aparece APENAS para o Anfitrião (created_by)
        // O Anfitrião é quem foi selecionado no campo "Host" ao criar a reunião
        return call.created_by === appUser.id;
      });

      // Collect all contact IDs from both sources
      const contactIds = [
        ...ringRequests.map((r: any) => r.contact_id),
        ...filteredScheduledCalls.map((c: any) => c.contact_id)
      ].filter((id): id is string => id !== null);

      // Collect all requester/creator IDs (use scheduled_by for scheduled calls with fallback to created_by)
      const requesterIds = [
        ...ringRequests.map((r: any) => r.requested_by),
        ...filteredScheduledCalls.map((c: any) => c.scheduled_by || c.created_by)
      ].filter((id): id is string => id !== null);

      // Fetch contact names and requester names in parallel
      const [contactsResult, requestersResult] = await Promise.all([
        contactIds.length > 0
          ? supabase
              .from('contacts')
              .select('id, first_name, last_name')
              .in('id', contactIds)
          : Promise.resolve({ data: [] }),
        requesterIds.length > 0
          ? supabase
              .from('app_users')
              .select('id, display_name, first_name, last_name')
              .in('id', requesterIds)
          : Promise.resolve({ data: [] })
      ]);

      const contactMap = new Map(
        (contactsResult.data || []).map(c => [
          c.id,
          `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Contato'
        ])
      );

      const requesterMap = new Map(
        (requestersResult.data || []).map(u => [
          u.id,
          u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown'
        ])
      );

      // Build confirmations from ring requests (Video Now)
      const ringConfirmations: PendingConfirmation[] = ringRequests.map((r: any) => ({
        id: r.id,
        contactId: r.contact_id,
        contactName: r.contact_id ? contactMap.get(r.contact_id) || 'Contato' : null,
        dealId: r.deal_id,
        leadId: r.lead_id,
        acceptedAt: r.accepted_at,
        meetingUrl: r.meeting_url,
        requestedBy: r.requested_by,
        requestedByName: requesterMap.get(r.requested_by) || null,
        source: 'ring' as const,
      }));

      // Build confirmations from scheduled calls (use scheduled_by for attribution with fallback to created_by)
      const scheduledConfirmations: PendingConfirmation[] = filteredScheduledCalls.map((c: any) => ({
        id: c.id,
        contactId: c.contact_id,
        contactName: c.contact_id ? contactMap.get(c.contact_id) || 'Contato' : null,
        dealId: c.deal_id,
        leadId: c.lead_id,
        acceptedAt: c.completed_at, // Use completed_at as the reference time
        completedAt: c.completed_at,
        meetingUrl: c.meeting_url,
        requestedBy: c.scheduled_by || c.created_by,
        requestedByName: requesterMap.get(c.scheduled_by || c.created_by) || null,
        source: 'scheduled' as const,
        scheduledTitle: c.title,
      }));

      // Combine, filter grace period for rings, and sort by time (most recent first)
      const now = Date.now();
      const allConfirmations = [...ringConfirmations, ...scheduledConfirmations]
        .filter(c => {
          // Grace period: don't show ring confirmations accepted less than 30s ago
          // This prevents the modal from appearing before the Daily video call opens
          if (c.source === 'ring') {
            const acceptedAgo = now - new Date(c.acceptedAt).getTime();
            return acceptedAgo > 30000;
          }
          return true;
        })
        .sort((a, b) => new Date(b.acceptedAt).getTime() - new Date(a.acceptedAt).getTime());

      setPendingConfirmations(allConfirmations);
    } catch (error) {
      console.error('Error loading pending confirmations:', error);
      setPendingConfirmations([]);
    } finally {
      setLoading(false);
    }
  }, [appUser?.id]);

  const confirmConnection = useCallback(async (
    confirmationId: string,
    confirmed: boolean,
    notes?: string,
    source: 'ring' | 'scheduled' = 'ring'
  ) => {
    if (!appUser?.id) return false;

    try {
      const table = source === 'ring' ? 'video_call_ring_requests' : 'calls';
      
      const { error } = await supabase
        .from(table as any)
        .update({
          connection_confirmed: confirmed,
          connection_confirmed_at: new Date().toISOString(),
          connection_confirmed_by: appUser.id,
          connection_notes: notes || null,
        })
        .eq('id', confirmationId);

      if (error) {
        console.error('Error confirming connection:', error);
        return false;
      }

      // For ring requests, also update the associated call record
      if (source === 'ring') {
        const { data: ringData } = await supabase
          .from('video_call_ring_requests' as any)
          .select('call_id')
          .eq('id', confirmationId)
          .single();

        if ((ringData as any)?.call_id) {
          await supabase
            .from('calls')
            .update({
              status: confirmed ? 'completed' : 'failed',
              completed_at: new Date().toISOString(),
              connection_confirmed: confirmed,
              connection_confirmed_at: new Date().toISOString(),
              connection_confirmed_by: appUser.id,
              connection_notes: notes || null,
            })
            .eq('id', (ringData as any).call_id);
        }
      }

      // Remove from pending list
      setPendingConfirmations(prev => prev.filter(p => p.id !== confirmationId));
      return true;
    } catch (error) {
      console.error('Error confirming connection:', error);
      return false;
    }
  }, [appUser?.id]);

  useEffect(() => {
    loadPendingConfirmations();

    // Set up real-time subscriptions for both tables
    // NOTE: No realtime for video_call_ring_requests to avoid race condition
    // with Daily video dialog focus trap. Polling every 60s is sufficient.

    const callsChannel = supabase
      .channel('pending-confirmations-calls')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'calls',
        },
        () => {
          loadPendingConfirmations();
        }
      )
      .subscribe();

    // Check periodically for expired confirmations
    const interval = setInterval(loadPendingConfirmations, 60000);

    return () => {
      callsChannel.unsubscribe();
      clearInterval(interval);
    };
  }, [loadPendingConfirmations]);

  return {
    pendingConfirmations,
    loading,
    confirmConnection,
    refresh: loadPendingConfirmations,
  };
}
